import { useEffect, useRef, useState } from "react";

import { mealChildProfilesEnabled } from "../lib/flags";
import { useLang } from "../lib/i18n";
import { childrenCopy } from "../lib/i18n/children";
import { useProfile } from "../lib/profile-context";

type Props = {
  // Called when the user picks "Add a child +" so the host can route to
  // the Children screen. Keeping routing concerns out of the switcher
  // mirrors the AdhdPill (presentational, host owns navigation).
  onOpenChildren: () => void;
};

// Compact pill that reveals the active profile and opens a dropdown of
// alternatives (self + each child + "add a child"). ADR-0018. Hidden when
// the feature flag is off — call sites still render it unconditionally;
// the gate lives here so future call sites pick it up automatically.
export function ProfileSwitcher({ onOpenChildren }: Props) {
  const { lang } = useLang();
  const t = childrenCopy[lang];
  const { active, children, setActive } = useProfile();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside-click. The dropdown lives inside this wrapper, so a
  // ref check is enough — no portal, no overlay needed.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (e.target instanceof Node && wrapperRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!mealChildProfilesEnabled()) return null;

  const activeLabel =
    active.kind === "self"
      ? t.chProfileSelf
      : (children.find((c) => c.id === active.childId)?.name ?? t.chProfileSelf);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.chSwitcherOpen}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 999,
          background: "var(--surface-2)",
          color: "var(--ink)",
          border: "none",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          cursor: "pointer",
          maxWidth: 160,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 99,
            background: "var(--accent)",
            flexShrink: 0,
          }}
        />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{activeLabel}</span>
        <span style={{ color: "var(--muted)", marginLeft: 2 }}>{open ? "▴" : "▾"}</span>
      </button>

      {open ? (
        <div
          role="listbox"
          aria-label={t.chSwitcherTitle}
          style={{
            // The switcher chip sits at the top-LEFT of the home header.
            // Anchoring the dropdown to the button's left edge keeps it on
            // screen on phones with narrow viewports (regression: a prior
            // `right: 0` clipped half the menu off the left side on S23).
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            minWidth: 180,
            background: "var(--surface)",
            borderRadius: 12,
            boxShadow: "0 0 0 1px var(--nav-border), 0 12px 32px rgba(0,0,0,0.16)",
            padding: 6,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <div
            style={{
              padding: "6px 10px 4px",
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              letterSpacing: "0.12em",
              color: "var(--muted)",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            {t.chSwitcherTitle}
          </div>
          <Option
            label={t.chProfileSelf}
            active={active.kind === "self"}
            onClick={() => {
              setActive({ kind: "self" });
              setOpen(false);
            }}
          />
          {children.map((c) => (
            <Option
              key={c.id}
              label={c.name}
              active={active.kind === "child" && active.childId === c.id}
              onClick={() => {
                setActive({ kind: "child", childId: c.id });
                setOpen(false);
              }}
            />
          ))}
          <div
            style={{
              marginTop: 4,
              borderTop: "1px solid var(--nav-border)",
              paddingTop: 4,
            }}
          >
            <Option
              label={t.chAddChildShort}
              accent
              onClick={() => {
                setOpen(false);
                onOpenChildren();
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Option({
  label,
  active,
  accent,
  onClick,
}: {
  label: string;
  active?: boolean;
  accent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 8,
        background: active ? "var(--surface-2)" : "transparent",
        color: accent ? "var(--accent)" : "var(--ink)",
        border: "none",
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        textAlign: "left",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 99,
          background: active ? "var(--accent)" : "transparent",
          boxShadow: active ? "none" : "inset 0 0 0 1px var(--nav-border)",
          flexShrink: 0,
        }}
      />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
    </button>
  );
}
